/**
 * Display labels for the transaction subject field.
 * Keys mirror the API enum stored as a free string column — unknown values fall
 * back to a humanized version of the raw value (see formatSubjectLabel below).
 */
export const SUBJECT_LABELS = {
    bank_transfer: "Virement bancaire",
    orange_money_transfer: "Orange Money",
    mobile_money_transfer: "Mobile Money",
    mvola_transfer: "MVola",
    airtel_money_transfer: "Airtel Money",
    refund: "Remboursement",
    payout: "Payout",
};

/**
 * Display labels for the transaction status field.
 */
export const STATUS_LABELS = {
    pending: "En attente",
    accepted: "Validé",
};

export const STATUS_COLORS = {
    pending: { bg: "rgba(255, 152, 0, 0.15)", color: "rgba(255, 183, 77, 1)", border: "rgba(255, 152, 0, 0.4)" },
    accepted: { bg: "rgba(76, 175, 80, 0.15)", color: "rgba(129, 199, 132, 1)", border: "rgba(76, 175, 80, 0.4)" },
};

/**
 * Transaction subjects paid manually to a mobile money phone number.
 * For these rows payout_id stores the destination phone snapshotted at
 * withdrawal request time (legacy rows may have a null payout_id).
 */
export const MOBILE_MONEY_SUBJECTS = [
    "orange_money_transfer",
    "mobile_money_transfer",
    "mvola_transfer",
    "airtel_money_transfer",
];

/**
 * Checks whether a subject is paid manually via mobile money.
 * @param {string} subject - Raw subject string from the API
 * @returns {boolean}
 */
export function isMobileMoneySubject(subject) {
    return MOBILE_MONEY_SUBJECTS.includes(subject);
}

/**
 * Returns the destination phone number for mobile money transactions.
 * payout_id only holds a phone number for mobile money subjects (it can
 * hold a Stripe payout ID on other rows), so filter by subject first.
 * @param {Object} transaction - Transaction object from the API
 * @returns {string|null}
 */
export function getPayoutPhone(transaction) {
    if (!transaction || !isMobileMoneySubject(transaction.subject)) return null;
    return transaction.payout_id || null;
}

/**
 * Formats a transaction amount with its wallet currency code.
 * The currency comes from the API enrichment (wallet currency); older
 * payloads without the field render the bare amount as before.
 * @param {Object} transaction - Transaction object from the API
 * @returns {string}
 */
export function formatAmount(transaction) {
    if (transaction?.amount == null) return "—";
    if (!transaction.currency) return String(transaction.amount);
    return `${transaction.amount} ${transaction.currency}`;
}

/**
 * Returns a display label for an arbitrary subject value.
 * Falls back to a humanized version (snake_case → "Snake Case") when no label is known.
 * @param {string} subject - Raw subject string from the API
 * @returns {string}
 */
export function formatSubjectLabel(subject) {
    if (!subject) return "—";
    if (SUBJECT_LABELS[subject]) return SUBJECT_LABELS[subject];
    return subject.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Number of pending transactions per page (mirrors the API server-side default). */
export const TRANSACTIONS_PAGE_SIZE = 20;
